/* @refresh reset */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { RootState } from '../../core/store/store';
import { useKeyboardShortcuts } from '../../shared/hooks/useKeyboardShortcuts';
import useUserProfileSync from '../../shared/hooks/useUserProfileSync';
import usePageTranslation from '../../shared/hooks/usePageTranslation';
import BuildingsModal from './components/BuildingsModal';
import WorldMap, { type LiveVehicleTrack } from './components/WorldMap';
import ContextMenu from './components/ContextMenu';
import GuidedTutorial from './components/GuidedTutorial';
import InterventionManageModal from './components/interventions/InterventionManageModal';
import { useInterventionManageController } from './components/interventions/useInterventionManageController';
import { Batiment, Personnel, Vehicule } from '../../shared/types/game';
import userService, { PanelAccess, RoleDefaultAccess } from '../../shared/services/userService';
import { openAppRouteInNewWindow } from '../../shared/utils/openAppRouteInNewWindow';
import { getApiBaseUrl, getSocketIoPath } from '../../core/config/api';
import config from '../../config/index';
import { io } from 'socket.io-client';
import BackgroundImage from '../../shared/components/BackgroundImage';
import {
  getInterventionDisplayLines,
  getInterventionPhaseUiKey,
  isTerminalInterventionStatus,
  mergeInterventionRow,
  type InstanceRow,
} from './components/interventions/instanceModel';

function formatDistanceKm(latA: number, lngA: number, latB: number, lngB: number): string {
  const toRad = (v: number) => (v * Math.PI) / 180;
  const r = 6371;
  const dLat = toRad(latB - latA);
  const dLng = toRad(lngB - lngA);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(latA)) * Math.cos(toRad(latB)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return `${(r * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(1)} km`;
}

function unitTypeLabel(buildingType: string): string {
  const key = String(buildingType || '').toLowerCase();
  if (key === 'pompier') return 'Incendie';
  if (key === 'hopital') return 'Secours à personne';
  if (key === 'fdo') return 'Police';
  if (key === 'prison') return 'Sécurité';
  if (key === 'cta') return 'Commandement';
  return 'Multirôle';
}

function isCtaBuildingType(buildingType: string | undefined): boolean {
  return String(buildingType || '').trim().toLowerCase() === 'cta';
}

function vehicleStateLabel(statut: string | undefined): 'PRÊT' | 'EN MISSION' | 'MAINTENANCE' | 'INDISPONIBLE' {
  const s = String(statut || '').toLowerCase();
  if (s === 'disponible') return 'PRÊT';
  if (s === 'en_mission') return 'EN MISSION';
  if (s === 'en_maintenance') return 'MAINTENANCE';
  return 'INDISPONIBLE';
}

function defaultPriorityForPhase(phase: string): number {
  if (phase === 'wait') return 1;
  if (phase === 'travel') return 2;
  return 3;
}

function interventionPhaseLabel(phase: string): string {
  if (phase === 'wait') return 'En attente';
  if (phase === 'travel') return 'En transit';
  if (phase === 'site') return 'Sur site';
  return 'En cours';
}

function interventionPhaseClass(phase: string): string {
  if (phase === 'wait') return 'border-red-500/40 bg-red-950/25 text-red-200';
  if (phase === 'travel') return 'border-amber-500/40 bg-amber-950/25 text-amber-200';
  if (phase === 'site') return 'border-emerald-500/40 bg-emerald-950/25 text-emerald-200';
  return 'border-slate-500/40 bg-slate-950/25 text-slate-200';
}

type MainMapView = 'dispatch' | 'resources' | 'buildings' | 'interventions';

function normalizeMainMapView(rawView: string | null): MainMapView {
  if (rawView === 'resources') return 'resources';
  if (rawView === 'buildings') return 'buildings';
  if (rawView === 'interventions') return 'interventions';
  return 'dispatch';
}

const Map: React.FC = () => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const { t, currentLang } = usePageTranslation('game', 'Map');
  // Ressources joueur (argent/médailles) visibles en quasi temps réel sur la carte.
  useUserProfileSync(2000, Boolean(currentUser?.id));
  const [searchParams, setSearchParams] = useSearchParams();
  const [showBuildingsModal, setShowBuildingsModal] = useState(false);
  const [buildings, setBuildings] = useState<Batiment[]>([]);
  const [playerVehicles, setPlayerVehicles] = useState<Vehicule[]>([]);
  const [playerPersonnel, setPlayerPersonnel] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState(true);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    lat: number;
    lng: number;
  } | null>(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState<string | null>(null);
  const [showInterventionSectors, setShowInterventionSectors] = useState(() =>
    typeof window !== 'undefined' && localStorage.getItem('mapShowInterventionSectors') === 'true'
  );
  const [interventions, setInterventions] = useState<InstanceRow[]>([]);
  const [liveVehicleTracks, setLiveVehicleTracks] = useState<LiveVehicleTrack[]>([]);
  const [selectedInterventionId, setSelectedInterventionId] = useState<number | null>(null);
  const [now, setNow] = useState(() => new Date());
  const [priseAppels, setPriseAppels] = useState(false);
  const [priseLoadError, setPriseLoadError] = useState<string | null>(null);
  const [priseToggling, setPriseToggling] = useState(false);
  const [maintenanceStatus, setMaintenanceStatus] = useState<{
    enabled: boolean;
    message: string | null;
    scheduled_at: string | null;
    scheduled_message: string | null;
  } | null>(null);
  const [buildingFilter, setBuildingFilter] = useState<number | 'all'>('all');
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState<string>('all');
  const [unitsFilter, setUnitsFilter] = useState<'all' | 'disponible' | 'en_mission' | 'en_maintenance'>('all');
  const [unitsSearch, setUnitsSearch] = useState('');
  const [interventionSearch, setInterventionSearch] = useState('');
  const [interventionPhaseFilter, setInterventionPhaseFilter] = useState<'all' | 'wait' | 'travel' | 'site'>('all');
  const [interventionSort, setInterventionSort] = useState<'priority' | 'newest' | 'oldest'>('priority');
  const [activeMainView, setActiveMainView] = useState<MainMapView>(() =>
    normalizeMainMapView(searchParams.get('view'))
  );
  const [futureNotice, setFutureNotice] = useState<string | null>(null);
  const [priorityOverrides, setPriorityOverrides] = useState<Record<number, number>>({});
  const [panelAccess, setPanelAccess] = useState<PanelAccess[]>([]);
  const [roleDefaultAccess, setRoleDefaultAccess] = useState<RoleDefaultAccess[]>([]);
  const [loadingPanelAccess, setLoadingPanelAccess] = useState(true);
  const titleLocale = currentLang === 'fr' ? 'fr-FR' : 'en-US';
  const previousBuildingsRef = useRef<Batiment[]>([]);
  const isInitialLoadRef = useRef(true);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const futureNoticeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Raccourcis clavier
  useKeyboardShortcuts({
    onMapToggle: () => navigate('/dashboard'),
    enabled:
      !showBuildingsModal &&
      !contextMenu,
  });

  const setMainView = useCallback((nextView: MainMapView) => {
    setActiveMainView(nextView);
    const nextParams = new URLSearchParams(searchParams);
    if (nextView === 'dispatch') {
      nextParams.delete('view');
    } else {
      nextParams.set('view', nextView);
    }
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const openDedicatedMainView = useCallback((view: MainMapView) => {
    const target = view === 'dispatch' ? '/game/map' : `/game/map?view=${view}`;
    openAppRouteInNewWindow(target);
  }, []);

  const openInterventionsSynoptic = useCallback(() => {
    setMainView('interventions');
  }, [setMainView]);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const loadInterventions = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/api/cta/interventions`, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (data?.success && Array.isArray(data?.interventions)) {
        setInterventions(data.interventions as InstanceRow[]);
      }
    } catch {
      // silencieux
    }
  }, [currentUser?.id, authHeaders]);

  const refreshInterventions = useCallback(async () => {
    await loadInterventions();
  }, [loadInterventions]);

  const loadLiveVehicles = useCallback(async () => {
    if (!currentUser?.id) {
      setLiveVehicleTracks([]);
      return;
    }
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
      if (!token) return;
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/api/cta/interventions-instance/live-vehicles`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success && Array.isArray(data?.vehicles)) {
        setLiveVehicleTracks(data.vehicles as LiveVehicleTrack[]);
      }
    } catch {
      setLiveVehicleTracks([]);
    }
  }, [currentUser?.id]);

  const applyLiveVehiclesDelta = useCallback(
    (data: {
      mode?: 'full' | 'delta';
      vehicles?: LiveVehicleTrack[];
      upserts?: LiveVehicleTrack[];
      removed_keys?: string[];
    }) => {
      const keyOf = (t: LiveVehicleTrack) => `${Number(t.intervention_id)}:${Number(t.vehicle_id)}`;
      if (data.mode === 'full' && Array.isArray(data.vehicles)) {
        setLiveVehicleTracks(data.vehicles);
        return;
      }
      if (data.mode === 'delta') {
        setLiveVehicleTracks((prev) => {
          const map = new globalThis.Map<string, LiveVehicleTrack>();
          for (const p of prev) map.set(keyOf(p), p);
          for (const u of data.upserts || []) map.set(keyOf(u), u);
          for (const rk of data.removed_keys || []) map.delete(String(rk));
          return Array.from(map.values());
        });
        return;
      }
      // Compatibilité si le payload ne porte pas encore de mode.
      if (Array.isArray(data.vehicles)) {
        setLiveVehicleTracks(data.vehicles);
      }
    },
    []
  );

  const manageController = useInterventionManageController({
    userId: currentUser?.id,
    instances: interventions,
    setInstances: setInterventions,
    loadInstances: loadInterventions,
    refreshInstances: refreshInterventions,
    authHeaders,
    t,
    titleLocale,
    fetchVehiclesEnabled: true,
  });

  useEffect(() => {
    const viewFromUrl = normalizeMainMapView(searchParams.get('view'));
    if (viewFromUrl !== activeMainView) {
      setActiveMainView(viewFromUrl);
    }
  }, [searchParams, activeMainView]);

  // Fonction pour sauvegarder les bâtiments en temps réel
  const saveBuildings = useCallback(async (buildingsToSave: Batiment[]) => {
    if (!currentUser) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Annuler la sauvegarde précédente si elle est en attente
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }

      // Sauvegarder immédiatement
      const response = await fetch(`${getApiBaseUrl(true)}/buildings/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ batiments: buildingsToSave })
      });

      if (!response.ok) {
        console.error('Erreur lors de la sauvegarde des bâtiments:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde automatique:', error);
    }
  }, [currentUser]);

  useEffect(() => {
    // Récupérer l'état du tutoriel depuis localStorage
    const savedTutorialEnabled = localStorage.getItem('tutorialEnabled') === 'true';
    
    // Afficher le tutoriel si le tutoriel est activé
    if (savedTutorialEnabled) {
      console.log('✅ Tutoriel activé !');
      setShowTutorial(true);
    } else {
      console.log('❌ Tutoriel désactivé');
      setShowTutorial(false);
    }

    // Charger les bâtiments de l'utilisateur
    const loadBuildings = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await fetch(`${getApiBaseUrl(true)}/buildings`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const [vehRes, persRes] = await Promise.all([
          fetch(`${getApiBaseUrl(true)}/buildings/vehicules`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }),
          fetch(`${getApiBaseUrl(true)}/buildings/personnel`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }),
        ]);

        if (response.ok) {
          const data = await response.json();
          console.log('📦 Données reçues de l\'API:', data);
          
          if (data.success && data.data) {
            const loadedBuildings = Array.isArray(data.data) ? data.data : [];
            console.log(`✅ ${loadedBuildings.length} bâtiment(s) chargé(s):`, loadedBuildings);
            setBuildings(loadedBuildings);
          } else {
            console.warn('⚠️ Aucun bâtiment dans la réponse:', data);
            setBuildings([]);
          }
        } else {
          const errorText = await response.text().catch(() => '');
          console.error('❌ Erreur lors du chargement des bâtiments:', response.status, response.statusText, errorText);
          setBuildings([]);
        }
        if (vehRes.ok) {
          const vehData = await vehRes.json().catch(() => ({}));
          if (vehData?.success && Array.isArray(vehData?.data)) {
            setPlayerVehicles(vehData.data as Vehicule[]);
          }
        }
        if (persRes.ok) {
          const persData = await persRes.json().catch(() => ({}));
          if (persData?.success && Array.isArray(persData?.data)) {
            setPlayerPersonnel(persData.data as Personnel[]);
          }
        }
      } catch (error: any) {
        console.error('❌ Erreur réseau lors du chargement des bâtiments:', error);
        setBuildings([]);
      } finally {
        setLoading(false);
      }
    };

    loadBuildings();

    // Écouter les événements de menu contextuel de la carte
    const handleMapContextMenu = (e: CustomEvent) => {
      setContextMenu({
        x: e.detail.x,
        y: e.detail.y,
        lat: e.detail.lat,
        lng: e.detail.lng
      });
    };

    window.addEventListener('mapcontextmenu', handleMapContextMenu as EventListener);

    return () => {
      window.removeEventListener('mapcontextmenu', handleMapContextMenu as EventListener);
    };
  }, [currentUser]);

  // Charger les interventions (pour affichage des icônes sur la carte)
  useEffect(() => {
    if (!currentUser) return;

    let cancelled = false;
    const wrappedLoad = async () => {
      if (cancelled) return;
      await loadInterventions();
    };
    void wrappedLoad();
    const id = window.setInterval(wrappedLoad, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [currentUser, loadInterventions]);

  /** Même logique que la barre d’interventions : événements temps réel + filtrage joueur. */
  useEffect(() => {
    const userId = currentUser?.id;
    if (!userId) return;

    const interventionSocketOwnerId = (payload: InstanceRow): number | undefined => {
      if (payload.joueur_id != null && Number.isFinite(Number(payload.joueur_id))) {
        return Number(payload.joueur_id);
      }
      if (payload.user_id != null && Number.isFinite(Number(payload.user_id))) {
        return Number(payload.user_id);
      }
      return undefined;
    };

    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
    const socket = io({
      path: getSocketIoPath(),
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
    });

    const upsert = (row: InstanceRow) => {
      setInterventions((prev) => {
        const i = prev.findIndex((x) => x.id === row.id);
        if (i === -1) return [row, ...prev];
        const next = [...prev];
        next[i] = mergeInterventionRow(next[i], row);
        return next;
      });
    };

    socket.on('connect', () => {
      socket.emit('join:user', userId);
      void loadLiveVehicles();
    });

    socket.on('intervention:new', (payload: InstanceRow) => {
      if (interventionSocketOwnerId(payload) !== userId) return;
      if (payload?.id) upsert(payload);
      void loadLiveVehicles();
    });

    socket.on('intervention:update', (payload: InstanceRow) => {
      if (interventionSocketOwnerId(payload) !== userId) return;
      if (payload?.id) upsert(payload);
      void loadLiveVehicles();
    });

    socket.on('cta:live-vehicles:update', (data: {
      mode?: 'full' | 'delta';
      vehicles?: LiveVehicleTrack[];
      upserts?: LiveVehicleTrack[];
      removed_keys?: string[];
    }) => {
      applyLiveVehiclesDelta(data || {});
    });

    socket.on('cta:prise-appels', (data: { prise_appels?: boolean }) => {
      if (typeof data?.prise_appels === 'boolean') {
        setPriseAppels(data.prise_appels);
        setPriseLoadError(null);
      }
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [currentUser?.id, loadLiveVehicles, applyLiveVehiclesDelta]);

  // Snapshot initial au montage (puis rafraîchi par tick serveur WebSocket).
  useEffect(() => {
    if (!currentUser?.id) {
      setLiveVehicleTracks([]);
      return;
    }
    void loadLiveVehicles();
  }, [currentUser?.id, loadLiveVehicles]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const userId = currentUser?.id;
    if (!userId) return;
    const key = `mapInterventionPriorities:${userId}`;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, number>;
      const normalized: Record<number, number> = {};
      Object.entries(parsed).forEach(([id, val]) => {
        const numericId = Number(id);
        const numericVal = Number(val);
        if (Number.isInteger(numericId) && numericId > 0 && Number.isFinite(numericVal) && numericVal > 0) {
          normalized[numericId] = Math.floor(numericVal);
        }
      });
      setPriorityOverrides(normalized);
    } catch {
      // Ignore local storage parse errors and continue with defaults.
    }
  }, [currentUser?.id]);

  const loadPriseAppels = useCallback(async () => {
    if (!currentUser?.id) return;
    setPriseLoadError(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
      if (!token) return;
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/api/cta/prise-appels`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success && typeof data?.prise_appels === 'boolean') {
        setPriseAppels(data.prise_appels);
      } else {
        setPriseLoadError('Impossible de charger l’état de prise d’appel.');
      }
    } catch {
      setPriseLoadError('Impossible de charger l’état de prise d’appel.');
    }
  }, [currentUser?.id]);

  const togglePriseAppels = useCallback(async () => {
    if (!currentUser?.id || priseToggling) return;
    setPriseToggling(true);
    setPriseLoadError(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
      if (!token) return;
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/api/cta/prise-appels`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prise_appels: !priseAppels }),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success && typeof data?.prise_appels === 'boolean') {
        setPriseAppels(data.prise_appels);
      } else {
        setPriseLoadError('Échec du changement de prise d’appel.');
      }
    } catch {
      setPriseLoadError('Échec du changement de prise d’appel.');
    } finally {
      setPriseToggling(false);
    }
  }, [currentUser?.id, priseAppels, priseToggling]);

  useEffect(() => {
    void loadPriseAppels();
  }, [loadPriseAppels]);

  useEffect(() => {
    const checkMaintenanceStatus = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000);
        const response = await fetch(`${getApiBaseUrl(true)}/admin/maintenance/status`, {
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (!response.ok) {
          if (response.status === 404) return;
          return;
        }
        const data = await response.json().catch(() => ({}));
        if (data?.success && data?.data) {
          setMaintenanceStatus({
            enabled: data.data.enabled === 1 || data.data.enabled === true,
            message: data.data.message || null,
            scheduled_at: data.data.scheduled_at || null,
            scheduled_message: data.data.scheduled_message || null,
          });
        }
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.debug('Maintenance status check failed on map:', error);
        }
      }
    };

    void checkMaintenanceStatus();
    const id = window.setInterval(checkMaintenanceStatus, 30000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const loadPanelAccess = async () => {
      if (!currentUser?.id || !currentUser?.roles_id) {
        setLoadingPanelAccess(false);
        return;
      }
      try {
        const [userAccessResponse, roleAccessResponse] = await Promise.all([
          userService.getUserPanelAccess(currentUser.id),
          userService.getRoleDefaultAccess(currentUser.roles_id),
        ]);
        if (userAccessResponse.success) {
          setPanelAccess(userAccessResponse.data);
        }
        if (roleAccessResponse.success) {
          setRoleDefaultAccess(roleAccessResponse.data);
        }
      } catch (error) {
        console.warn('Impossible de charger les droits panel depuis la carte:', error);
      } finally {
        setLoadingPanelAccess(false);
      }
    };
    void loadPanelAccess();
  }, [currentUser?.id, currentUser?.roles_id]);

  // Sauvegarder automatiquement lorsqu'il y a des changements dans les bâtiments (fallback)
  useEffect(() => {
    // Ignorer le premier rendu (chargement initial)
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      previousBuildingsRef.current = buildings;
      return;
    }

    // Vérifier s'il y a eu des changements
    const hasChanged = JSON.stringify(buildings) !== JSON.stringify(previousBuildingsRef.current);
    
    if (hasChanged && buildings.length >= 0 && currentUser) {
      // Sauvegarder en temps réel
      saveBuildings(buildings);
      previousBuildingsRef.current = buildings;
    }
  }, [buildings, currentUser, saveBuildings]);

  // Nettoyer le timeout lors du démontage
  useEffect(() => {
    const timeoutRef = saveTimeoutRef.current;
    return () => {
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
      if (futureNoticeTimeoutRef.current) {
        clearTimeout(futureNoticeTimeoutRef.current);
      }
    };
  }, []);

  // Vérifier l'état du tutoriel au chargement
  useEffect(() => {
    const savedTutorialEnabled = localStorage.getItem('tutorialEnabled') === 'true';
    
    if (savedTutorialEnabled) {
      console.log('✅ Activation du tutoriel !');
      setShowTutorial(true);
    } else {
      console.log('❌ Tutoriel non activé');
      setShowTutorial(false);
    }
  }, []);

  // Log de l'état au rendu
  useEffect(() => {
    console.log('🎯 État du tutoriel au rendu:', { 
      showTutorial
    });
  }, [showTutorial]);

  // Mettre en évidence les éléments du tutoriel
  useEffect(() => {
    if (highlightedElement) {
      const element = document.getElementById(highlightedElement);
      if (element) {
        element.classList.add('tutorial-highlight');
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      // Retirer tous les highlights
      document.querySelectorAll('.tutorial-highlight').forEach(el => {
        el.classList.remove('tutorial-highlight');
      });
    }

    return () => {
      document.querySelectorAll('.tutorial-highlight').forEach(el => {
        el.classList.remove('tutorial-highlight');
      });
    };
  }, [highlightedElement]);

  const handleBuildingPlace = async (lat: number, lng: number, buildingType: string, buildingName: string) => {
    if (!currentUser) return;

    // Fermer le menu contextuel
    setContextMenu(null);

    // Nom depuis la BDD (config_batiments), sinon fallback sur le code
    const displayName = buildingName?.trim() || buildingType;

    // Compter combien de bâtiments de ce type existent déjà
    const existingCount = buildings.filter(b => b.type === buildingType).length;

    // Capacité de base par niveau (exemple: niveau 1 = 10 postes, chaque niveau ajoute 5)
    const baseCapacityPerLevel = config.buildings.capacity.baseCapacityPerLevel;
    const capacityIncreasePerLevel = config.buildings.capacity.capacityIncreasePerLevel;
    const initialLevel = config.buildings.defaults.initialLevel;
    const initialCapacity = baseCapacityPerLevel + (initialLevel - 1) * capacityIncreasePerLevel;
    const initialVehiclesCapacity = initialCapacity; // Même capacité de base pour les véhicules
    
    // Constantes pour le système d'XP
    const XP_REQUIS_PAR_NIVEAU = config.buildings.experience.xpRequiredPerLevel;

    // Créer un nouveau bâtiment avec les informations de base
    const newBuilding: Batiment = {
      id: Date.now(), // ID temporaire (sera remplacé par l'ID de la base de données lors de la sauvegarde)
      nom: `${displayName} #${existingCount + 1}`,
      type: buildingType,
      niveau: initialLevel,
      user_id: currentUser.id,
      localisation: `${lat},${lng}`, // Stocker les coordonnées sous forme de string "lat,lng"
      capacite: initialCapacity,
      capacite_vehicules: initialVehiclesCapacity,
      cout: 0,
      description: '',
      actif: true,
      postes_disponibles: initialCapacity,
      postes_occupes: 0,
      augmentations_capacite_personnel: 0,
      augmentations_capacite_vehicules: 0,
      augmentation_personnel_en_cours: null,
      augmentation_vehicules_en_cours: null,
      xp: 0,
      xp_requis_par_niveau: XP_REQUIS_PAR_NIVEAU,
      created_at: new Date().toISOString(),
    };

    // Ajouter le bâtiment à la liste
    const updatedBuildings = [...buildings, newBuilding];
    setBuildings(updatedBuildings);

    // Sauvegarder immédiatement en temps réel
    await saveBuildings(updatedBuildings);
  };

  const handleBuildingsClick = () => {
    // Ouvrir simplement la modale des bâtiments
    setShowBuildingsModal(true);
  };

  const handleFutureFeature = useCallback((featureLabel: string) => {
    setFutureNotice(`${featureLabel} : en cours de mise a jour, disponible prochainement.`);
    if (futureNoticeTimeoutRef.current) {
      clearTimeout(futureNoticeTimeoutRef.current);
    }
    futureNoticeTimeoutRef.current = setTimeout(() => {
      setFutureNotice(null);
      futureNoticeTimeoutRef.current = null;
    }, 3500);
  }, []);

  const activeInterventions = useMemo(
    () =>
      [...interventions]
        .filter((inv) => !isTerminalInterventionStatus(inv.statut || ''))
        .sort((a, b) => {
          const ta = a.date_creation ? new Date(a.date_creation).getTime() : 0;
          const tb = b.date_creation ? new Date(b.date_creation).getTime() : 0;
          return tb - ta;
        }),
    [interventions]
  );

  useEffect(() => {
    if (activeInterventions.length === 0) {
      setSelectedInterventionId(null);
      return;
    }
    if (selectedInterventionId == null || !activeInterventions.some((inv) => inv.id === selectedInterventionId)) {
      setSelectedInterventionId(activeInterventions[0].id);
    }
  }, [activeInterventions, selectedInterventionId]);

  const selectedIntervention =
    activeInterventions.find((inv) => inv.id === selectedInterventionId) ?? activeInterventions[0] ?? null;
  const selectedDisplay = selectedIntervention
    ? getInterventionDisplayLines(selectedIntervention, 'Intervention')
    : null;
  const availablePersonnel = playerPersonnel.filter((p) => p.actif).length;
  const availableVehicles = playerVehicles.length;
  const headerClock = now.toLocaleString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const availabilityPercent = Math.min(99, 60 + activeInterventions.length);
  const availabilityCircumference = 2 * Math.PI * 30;
  const availabilityOffset = availabilityCircumference * (1 - availabilityPercent / 100);
  const availableUnitsRows = useMemo(() => {
    const normalizedSearch = unitsSearch.trim().toLowerCase();
    const normalizedTypeFilter = vehicleTypeFilter.trim().toLowerCase();
    const rows = playerVehicles
      .filter((v) => {
        const linkedBuilding = buildings.find((b) => Number(b.id) === Number(v.batiment_id));
        if (buildingFilter === 'all') {
          // En mode "Tous les bâtiments", masquer les véhicules rattachés à un CTA.
          return !isCtaBuildingType(linkedBuilding?.type);
        }
        return Number(v.batiment_id) === Number(buildingFilter);
      })
      .filter((v) =>
        normalizedTypeFilter === 'all'
          ? true
          : String(v.type || '').trim().toLowerCase() === normalizedTypeFilter
      )
      .filter((v) => (unitsFilter === 'all' ? true : String(v.statut) === unitsFilter))
      .filter((v) => {
        if (!normalizedSearch) return true;
        return (
          String(v.nom || '').toLowerCase().includes(normalizedSearch) ||
          String(v.type || '').toLowerCase().includes(normalizedSearch)
        );
      })
      .slice(0, 24)
      .map((v, idx) => {
        const state = vehicleStateLabel(v.statut);
        const building = buildings.find((b) => Number(b.id) === Number(v.batiment_id));
        const service = building ? unitTypeLabel(building.type) : 'Non assigne';
        const crewTotal = Math.max(2, Number(v.capacite || 2));
        const crewDispo = state === 'PRÊT' ? Math.max(1, crewTotal - (idx % 2)) : Math.max(0, crewTotal - 2);
        return {
          id: v.id,
          unit: v.nom || `Unité ${idx + 1}`,
          type: String(v.type || unitTypeLabel(building?.type || '')),
          service,
          crew: `${crewDispo}/${crewTotal}`,
          state,
          eta: state === 'PRÊT' ? `${2 + (idx % 5)} min` : '—',
        };
      });
    return rows;
  }, [playerVehicles, buildingFilter, vehicleTypeFilter, unitsFilter, unitsSearch, buildings]);
  const buildingFilterOptions = useMemo(
    () =>
      buildings
        .filter((b) => !isCtaBuildingType(b.type))
        .map((b) => ({ id: Number(b.id), name: b.nom || `Bâtiment ${b.id}` }))
        .sort((a, b) => a.name.localeCompare(b.name, 'fr')),
    [buildings]
  );
  const vehicleTypeFilterOptions = useMemo(() => {
    const types = Array.from(
      new Set(
        playerVehicles
          .map((v) => String(v.type || '').trim())
          .filter((v) => v.length > 0)
      )
    );
    types.sort((a, b) => a.localeCompare(b, 'fr'));
    return types;
  }, [playerVehicles]);
  const selectedInterventionVehicleRows = useMemo(() => {
    if (!selectedIntervention?.dispatch_player_vehicle_ids?.length) return [];
    const ids = new Set(selectedIntervention.dispatch_player_vehicle_ids.map((x) => Number(x)));
    return playerVehicles.filter((v) => ids.has(Number(v.id)));
  }, [selectedIntervention, playerVehicles]);
  const vehicleStatusMetrics = useMemo(() => {
    const total = playerVehicles.length;
    const pret = playerVehicles.filter((v) => String(v.statut) === 'disponible').length;
    const mission = playerVehicles.filter((v) => String(v.statut) === 'en_mission').length;
    const maintenance = playerVehicles.filter((v) => String(v.statut) === 'en_maintenance').length;
    const panne = playerVehicles.filter((v) => String(v.statut) === 'panne').length;
    const premium = playerVehicles.filter((v) => Boolean(v.premium)).length;
    const totalKm = playerVehicles.reduce((sum, v) => sum + Math.max(0, Number(v.kilometrage || 0)), 0);
    const avgKm = total > 0 ? Math.round(totalKm / total) : 0;
    const totalCapacity = playerVehicles.reduce((sum, v) => sum + Math.max(1, Number(v.capacite || 2)), 0);
    return {
      total,
      pret,
      mission,
      maintenance,
      panne,
      premium,
      totalKm,
      avgKm,
      totalCapacity,
      readinessPercent: total > 0 ? Math.round((pret / total) * 100) : 0,
    };
  }, [playerVehicles]);
  const personnelMetrics = useMemo(() => {
    const total = playerPersonnel.length;
    const actif = playerPersonnel.filter((p) => Boolean(p.actif)).length;
    const inactif = Math.max(0, total - actif);
    const avgAge = total > 0 ? Math.round(playerPersonnel.reduce((sum, p) => sum + Math.max(0, Number(p.age || 0)), 0) / total) : 0;
    const nearRetirement = playerPersonnel.filter((p) => Number(p.age_retraite || 0) - Number(p.age || 0) <= 5).length;
    const premium = playerPersonnel.filter((p) => Boolean(p.premium)).length;
    return {
      total,
      actif,
      inactif,
      avgAge,
      nearRetirement,
      premium,
      activeRate: total > 0 ? Math.round((actif / total) * 100) : 0,
    };
  }, [playerPersonnel]);
  const buildingOperationalRows = useMemo(() => {
    return buildings
      .filter((b) => !isCtaBuildingType(b.type))
      .map((building) => {
        const linkedVehicles = playerVehicles.filter((v) => Number(v.batiment_id || 0) === Number(building.id));
        const linkedPersonnel = playerPersonnel.filter((p) => Number(p.batiment_id || 0) === Number(building.id));
        const vehCapacity = Math.max(0, Number(building.capacite_vehicules || 0));
        const personnelSlots = Math.max(0, Number(building.postes_disponibles || 0));
        const personnelOccupied = Math.max(0, Number(building.postes_occupes || 0));
        const vehicleLoadPercent = vehCapacity > 0 ? Math.min(100, Math.round((linkedVehicles.length / vehCapacity) * 100)) : 0;
        const personnelLoadPercent = personnelSlots > 0 ? Math.min(100, Math.round((personnelOccupied / personnelSlots) * 100)) : 0;
        const xp = Math.max(0, Number(building.xp || 0));
        const xpRequired = Math.max(1, Number(building.xp_requis_par_niveau || 10));
        const xpPercent = Math.min(100, Math.round((xp / xpRequired) * 100));
        const personnelActive = linkedPersonnel.filter((p) => p.actif).length;
        return {
          id: building.id,
          name: building.nom || `Bâtiment ${building.id}`,
          typeLabel: unitTypeLabel(building.type),
          level: Number(building.niveau || 1),
          vehiclesCount: linkedVehicles.length,
          vehiclesReady: linkedVehicles.filter((v) => String(v.statut) === 'disponible').length,
          vehicleCapacity: vehCapacity,
          vehicleLoadPercent,
          personnelCount: linkedPersonnel.length,
          personnelActive,
          personnelSlots,
          personnelOccupied,
          personnelLoadPercent,
          xp,
          xpRequired,
          xpPercent,
          isPersonnelUpgradeRunning: Boolean(building.augmentation_personnel_en_cours),
          isVehicleUpgradeRunning: Boolean(building.augmentation_vehicules_en_cours),
        };
      })
      .sort((a, b) => {
        if (b.level !== a.level) return b.level - a.level;
        return a.name.localeCompare(b.name, 'fr');
      });
  }, [buildings, playerVehicles, playerPersonnel]);
  const buildingTypeBreakdown = useMemo(() => {
    const grouped: Record<string, { typeName: string; count: number; vehicles: number; personnel: number }> = {};
    buildingOperationalRows.forEach((row) => {
      const current = grouped[row.typeLabel] || { typeName: row.typeLabel, count: 0, vehicles: 0, personnel: 0 };
      current.count += 1;
      current.vehicles += row.vehiclesCount;
      current.personnel += row.personnelCount;
      grouped[row.typeLabel] = current;
    });
    return Object.values(grouped).sort((a, b) => b.count - a.count || a.typeName.localeCompare(b.typeName, 'fr'));
  }, [buildingOperationalRows]);
  const resourceAlerts = useMemo(() => {
    const alerts: string[] = [];
    if (vehicleStatusMetrics.mission >= Math.max(3, Math.ceil(vehicleStatusMetrics.total * 0.35))) {
      alerts.push('Charge terrain elevee : une part importante de la flotte est deja engagee.');
    }
    if (vehicleStatusMetrics.maintenance + vehicleStatusMetrics.panne >= Math.max(2, Math.ceil(vehicleStatusMetrics.total * 0.25))) {
      alerts.push('Tension maintenance : plusieurs vehicules sont indisponibles (maintenance/panne).');
    }
    if (personnelMetrics.nearRetirement > 0) {
      alerts.push(`${personnelMetrics.nearRetirement} agent(s) proche(s) de la retraite : anticiper la releve RH.`);
    }
    if (activeInterventions.length >= 5) {
      alerts.push('Pic d activite : volume d interventions eleve sur la carte.');
    }
    return alerts.slice(0, 4);
  }, [vehicleStatusMetrics, personnelMetrics, activeInterventions.length]);
  const topMileageVehicles = useMemo(
    () =>
      [...playerVehicles]
        .sort((a, b) => Number(b.kilometrage || 0) - Number(a.kilometrage || 0))
        .slice(0, 5),
    [playerVehicles]
  );
  const getInterventionPriority = useCallback(
    (inv: InstanceRow): number => {
      const override = priorityOverrides[inv.id];
      if (Number.isFinite(override) && override > 0) return Math.floor(override);
      const fromRow = Number(inv.priorite);
      if (Number.isFinite(fromRow) && fromRow > 0) return Math.floor(fromRow);
      return defaultPriorityForPhase(getInterventionPhaseUiKey(inv));
    },
    [priorityOverrides]
  );
  const filteredInterventions = useMemo(() => {
    const normalizedSearch = interventionSearch.trim().toLowerCase();
    const rows = activeInterventions.filter((inv) => {
      const phase = getInterventionPhaseUiKey(inv);
      if (interventionPhaseFilter !== 'all' && phase !== interventionPhaseFilter) {
        return false;
      }
      if (!normalizedSearch) return true;
      const lines = getInterventionDisplayLines(inv, 'Intervention');
      return (
        lines.headline.toLowerCase().includes(normalizedSearch) ||
        lines.subline.toLowerCase().includes(normalizedSearch) ||
        String(inv.numero_intervention || '').toLowerCase().includes(normalizedSearch)
      );
    });
    rows.sort((a, b) => {
      if (interventionSort === 'priority') {
        const pa = getInterventionPriority(a);
        const pb = getInterventionPriority(b);
        if (pa !== pb) return pa - pb;
      }
      const ta = a.date_creation ? new Date(a.date_creation).getTime() : 0;
      const tb = b.date_creation ? new Date(b.date_creation).getTime() : 0;
      if (interventionSort === 'oldest') return ta - tb;
      return tb - ta;
    });
    return rows;
  }, [
    activeInterventions,
    interventionSearch,
    interventionPhaseFilter,
    interventionSort,
    getInterventionPriority,
  ]);
  const interventionPhaseCounts = useMemo(() => {
    const counts = { wait: 0, travel: 0, site: 0 };
    activeInterventions.forEach((inv) => {
      const phase = getInterventionPhaseUiKey(inv);
      if (phase === 'wait' || phase === 'travel' || phase === 'site') {
        counts[phase] += 1;
      }
    });
    return counts;
  }, [activeInterventions]);
  const setInterventionPriority = useCallback(
    (inv: InstanceRow, nextPriorityRaw: number) => {
      const userId = currentUser?.id;
      if (!userId) return;
      if (isTerminalInterventionStatus(inv.statut || '')) return;
      const nextPriority = Math.max(1, Math.floor(Number(nextPriorityRaw) || 1));
      setPriorityOverrides((prev) => {
        const next = { ...prev, [inv.id]: nextPriority };
        localStorage.setItem(`mapInterventionPriorities:${userId}`, JSON.stringify(next));
        return next;
      });
      setInterventions((prev) =>
        prev.map((row) => (row.id === inv.id ? { ...row, priorite: nextPriority } : row))
      );
    },
    [currentUser?.id]
  );
  useEffect(() => {
    const userId = currentUser?.id;
    if (!userId) return;
    setPriorityOverrides((prev) => {
      const activeIds = new Set(activeInterventions.map((inv) => inv.id));
      const next: Record<number, number> = {};
      Object.entries(prev).forEach(([idStr, value]) => {
        const id = Number(idStr);
        if (activeIds.has(id)) next[id] = value;
      });
      localStorage.setItem(`mapInterventionPriorities:${userId}`, JSON.stringify(next));
      return next;
    });
  }, [activeInterventions, currentUser?.id]);
  const isAdmin = Boolean(currentUser?.roles_id && currentUser.roles_id <= 2);
  const hasAccessToPanelSection = useCallback(
    (sectionId: string, defaultAccess: boolean): boolean => {
      const customAccess = panelAccess.find((a) => a.section === sectionId);
      if (customAccess !== undefined) return customAccess.has_access;
      const roleAccess = roleDefaultAccess.find((a) => a.section === sectionId);
      if (roleAccess !== undefined) return roleAccess.has_access;
      return defaultAccess;
    },
    [panelAccess, roleDefaultAccess]
  );
  const hasPanelEntryAccess = useMemo(() => {
    const rolesId = currentUser?.roles_id ?? 0;
    const hasAdminAccess = hasAccessToPanelSection('admin', isAdmin);
    const hasModerationAccess = hasAccessToPanelSection('moderation', rolesId <= 3);
    const hasStatsAccess = hasAccessToPanelSection('stats', true);
    const hasInterventionsAccess = hasAccessToPanelSection('interventions', true);
    const hasSystemAccess = hasAccessToPanelSection('system', rolesId === 1);
    return hasAdminAccess || hasModerationAccess || hasStatsAccess || hasInterventionsAccess || hasSystemAccess;
  }, [currentUser?.roles_id, hasAccessToPanelSection, isAdmin]);
  const showScheduledBanner = Boolean(maintenanceStatus?.scheduled_at && !maintenanceStatus?.enabled);
  const showMaintenanceBanner = Boolean(maintenanceStatus?.enabled);
  const maintenanceBannerOffset = (showScheduledBanner ? 56 : 0) + (showMaintenanceBanner ? 56 : 0);

  if (!currentUser) {
    return null;
  }

  if (loading) {
    return (
      <div className="fixed inset-0 w-full h-full flex items-center justify-center bg-gray-900">
        <div className="text-white text-lg">Chargement de la carte...</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 overflow-hidden bg-slate-950/92 text-slate-100">
      <BackgroundImage />
      <header className="absolute inset-x-0 top-0 z-[1200] border-b border-slate-600/45 bg-gradient-to-b from-slate-700/95 to-slate-900/95 backdrop-blur-md">
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/images/logo.png" alt="Logo" className="h-8 w-8 rounded-sm object-contain" />
              <div>
                <p className="text-[9px] uppercase tracking-[0.22em] text-slate-300">Centre de commandement</p>
                <p className="text-sm font-bold tracking-wide text-white">OPSECOURS WEB</p>
              </div>
            </Link>
            <div className="ml-2 flex items-center gap-2 border-l border-slate-600/70 pl-3 text-[11px]">
              <div className="rounded-md border border-emerald-500/40 bg-emerald-900/25 px-2 py-1 font-semibold text-emerald-100">
                € {Number(currentUser.argents ?? 0).toLocaleString('fr-FR')}
              </div>
              <div className="rounded-md border border-amber-500/40 bg-amber-900/20 px-2 py-1 font-semibold text-amber-100">
                🏅 {Number(currentUser.medailles ?? 0).toLocaleString('fr-FR')}
              </div>
            </div>
          </div>
          <div className="flex flex-1 items-center justify-end overflow-x-auto">
            <div className="flex min-w-max items-center gap-2 py-1 pl-3 text-[11px]">
              <time className="hidden text-slate-300 sm:block">{headerClock}</time>
              <div className="flex items-center gap-2 rounded-md border border-slate-600/60 bg-slate-800/70 px-2 py-1">
                <span className={`text-[10px] font-semibold uppercase tracking-wide ${showInterventionSectors ? 'text-emerald-300' : 'text-amber-300'}`}>
                  Secteurs {showInterventionSectors ? 'ON' : 'OFF'}
                </span>
                <button
                  type="button"
                  id="map-toggle-sectors"
                  role="switch"
                  aria-checked={showInterventionSectors}
                  onClick={() => {
                    setShowInterventionSectors((prev) => {
                      const next = !prev;
                      localStorage.setItem('mapShowInterventionSectors', next ? 'true' : 'false');
                      return next;
                    });
                  }}
                  className={`relative h-5 w-10 rounded-full transition-colors ${
                    showInterventionSectors ? 'bg-emerald-600' : 'bg-slate-600'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
                      showInterventionSectors ? 'left-5' : 'left-0.5'
                    }`}
                  />
                </button>
              </div>
              <div className="flex items-center gap-2 rounded-md border border-slate-600/60 bg-slate-800/70 px-2 py-1">
                <span className={`text-[10px] font-semibold uppercase tracking-wide ${priseAppels ? 'text-emerald-300' : 'text-amber-300'}`}>
                  Appels {priseAppels ? 'ON' : 'OFF'}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={priseAppels}
                  onClick={() => void togglePriseAppels()}
                  disabled={priseToggling}
                  className={`relative h-5 w-10 rounded-full transition-colors disabled:opacity-50 ${
                    priseAppels ? 'bg-emerald-600' : 'bg-slate-600'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
                      priseAppels ? 'left-5' : 'left-0.5'
                    }`}
                  />
                </button>
              </div>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="rounded-md border border-slate-500/60 bg-slate-800/70 px-2 py-1 font-semibold text-slate-100 hover:bg-slate-700/80"
              >
                Retour dashboard
              </button>
              {!loadingPanelAccess && hasPanelEntryAccess ? (
                <button
                  type="button"
                  onClick={() => openAppRouteInNewWindow('/panel')}
                  className="rounded-md border border-indigo-500/60 bg-indigo-600/25 px-2 py-1 font-semibold text-indigo-100 hover:bg-indigo-600/35"
                >
                  Accès panel
                </button>
              ) : null}
              <div className="rounded-md border border-emerald-500/50 bg-emerald-500/15 px-2 py-1 font-semibold uppercase tracking-wide text-emerald-100">
                {currentUser.username} · en ligne
              </div>
              {priseLoadError ? <span className="hidden text-[10px] text-red-300 xl:inline">{priseLoadError}</span> : null}
            </div>
          </div>
        </div>
        <div className="border-t border-slate-700/60 px-2 py-1">
          <div className="overflow-x-auto">
            <nav className="flex min-w-max items-center gap-1 text-[11px]">
              {['Dispatch', 'Ressources', 'Bâtiments', 'Synoptique interventions', 'Alliances', 'Formations', 'Rapports', 'Météo'].map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    if (item === 'Dispatch') {
                      setMainView('dispatch');
                      return;
                    }
                    if (item === 'Ressources') {
                      setMainView('resources');
                      return;
                    }
                    if (item === 'Bâtiments') {
                      setMainView('buildings');
                      return;
                    }
                    if (item === 'Synoptique interventions') {
                      setMainView('interventions');
                      return;
                    }
                    if (item === 'Alliances' || item === 'Formations' || item === 'Météo' || item === 'Rapports') {
                      handleFutureFeature(item);
                    }
                  }}
                  className={`rounded-md border px-2.5 py-1 font-semibold uppercase tracking-wide transition-colors ${
                    (item === 'Dispatch' && activeMainView === 'dispatch') ||
                    (item === 'Ressources' && activeMainView === 'resources') ||
                    (item === 'Bâtiments' && activeMainView === 'buildings') ||
                    (item === 'Synoptique interventions' && activeMainView === 'interventions')
                      ? 'border-emerald-500/60 bg-emerald-500/20 text-emerald-100'
                      : item === 'Alliances' || item === 'Formations' || item === 'Météo' || item === 'Rapports'
                        ? 'border-slate-700/60 bg-slate-800/20 text-slate-500 hover:text-slate-400'
                        : 'border-slate-600/50 bg-slate-800/35 text-slate-300 hover:text-white'
                  }`}
                >
                  {item}
                  {(item === 'Alliances' || item === 'Formations' || item === 'Météo' || item === 'Rapports') ? ' (bientot)' : ''}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </header>

      {futureNotice ? (
        <div className="absolute right-4 top-24 z-[1250] rounded-md border border-amber-500/45 bg-amber-950/90 px-3 py-2 text-xs text-amber-200 shadow-lg shadow-black/30">
          {futureNotice}
        </div>
      ) : null}

      <div className="absolute inset-x-0 bottom-0 p-3" style={{ top: `${88 + maintenanceBannerOffset}px` }}>
        {showScheduledBanner ? (
          <div className="mb-2 rounded-md border border-amber-500/50 bg-amber-900/85 px-3 py-2 text-xs text-amber-100 shadow-lg shadow-black/25">
            📅 {maintenanceStatus?.scheduled_message || 'Maintenance programmée'}{' '}
            {maintenanceStatus?.scheduled_at
              ? `— ${new Date(maintenanceStatus.scheduled_at).toLocaleString('fr-FR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}`
              : ''}
          </div>
        ) : null}
        {showMaintenanceBanner ? (
          <div className="mb-2 rounded-md border border-orange-500/50 bg-orange-900/85 px-3 py-2 text-xs text-orange-100 shadow-lg shadow-black/25">
            🔧 {maintenanceStatus?.message || 'Maintenance en cours'}
          </div>
        ) : null}
        {activeMainView === 'dispatch' ? (
        <div className="grid h-full grid-cols-[300px,minmax(0,1fr),420px] gap-3">
          <aside className="flex min-h-0 flex-col gap-3">
            <section className="rounded-lg border border-slate-700/75 bg-slate-900/90 shadow-xl shadow-black/25">
              <div className="border-b border-slate-700/70 px-3 py-2">
                <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-red-300">
                  Interventions en cours ({activeInterventions.length})
                </h2>
              </div>
              <div className="max-h-[42vh] space-y-2 overflow-y-auto p-2">
                {activeInterventions.slice(0, 7).map((inv, idx) => {
                  const lines = getInterventionDisplayLines(inv, 'Intervention');
                  const priority = getInterventionPriority(inv);
                  return (
                    <button
                      key={inv.id}
                      type="button"
                      onClick={() => setSelectedInterventionId(inv.id)}
                      className={`w-full rounded-md border p-2 text-left transition-colors ${
                        selectedIntervention?.id === inv.id
                          ? 'border-red-500/70 bg-red-950/35'
                          : 'border-slate-700/80 bg-slate-950/70 hover:bg-slate-800/80'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-xs font-bold uppercase text-slate-100">{lines.headline}</p>
                          <p className="mt-0.5 line-clamp-1 text-[10px] text-slate-400">{lines.subline || 'Adresse non précisée'}</p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="rounded border border-red-500/45 bg-red-500/20 px-1.5 py-0.5 text-[9px] font-semibold text-red-200">
                            Priorité
                          </span>
                          <input
                            type="number"
                            min={1}
                            step={1}
                            value={priority}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setInterventionPriority(inv, Number(e.target.value))}
                            className="w-14 rounded border border-red-500/45 bg-slate-900/95 px-1.5 py-0.5 text-[10px] font-semibold text-red-200 outline-none focus:border-red-400"
                            title="Priorité modifiable par le joueur"
                          />
                        </div>
                      </div>
                      <div className="mt-1.5 flex items-center justify-between text-[10px] text-slate-400">
                        <span>#{inv.numero_intervention || inv.id}</span>
                        <span>{formatDistanceKm(45.764, 4.8357, Number(inv.latitude || 45.764), Number(inv.longitude || 4.8357))}</span>
                        <span>{2 + idx} min</span>
                      </div>
                    </button>
                  );
                })}
                {activeInterventions.length === 0 ? (
                  <p className="rounded-md border border-slate-700/80 bg-slate-950/70 px-2 py-6 text-center text-xs text-slate-500">
                    Aucune intervention active
                  </p>
                ) : null}
              </div>
            </section>

            <section className="min-h-0 flex-1 rounded-lg border border-slate-700/75 bg-slate-900/90 shadow-xl shadow-black/25">
              <div className="border-b border-slate-700/70 px-3 py-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-200">
                  {selectedDisplay?.headline || 'Intervention'}
                </h3>
              </div>
              <div className="space-y-3 p-3 text-sm">
                <div className="grid grid-cols-3 gap-1 rounded-md border border-slate-700/75 bg-slate-950/80 p-1 text-[10px] uppercase tracking-wide">
                  <span className="rounded bg-slate-800 px-2 py-1 text-center text-slate-200">Détails</span>
                  <span className="rounded px-2 py-1 text-center text-slate-500">Notes</span>
                  <span className="rounded px-2 py-1 text-center text-slate-500">Historique</span>
                </div>
                <div className="rounded-md border border-slate-700/80 bg-slate-950/70 p-2.5 text-[11px]">
                  <p className="text-slate-500">Adresse</p>
                  <p className="mt-0.5 text-slate-100">{selectedDisplay?.subline || 'Non renseignée'}</p>
                  <p className="mt-2 text-slate-500">Type</p>
                  <p className="mt-0.5 text-slate-100">{selectedDisplay?.headline || 'Intervention'}</p>
                  <p className="mt-2 text-slate-500">Appel reçu</p>
                  <p className="mt-0.5 text-slate-100">
                    {selectedIntervention?.date_creation
                      ? new Date(selectedIntervention.date_creation).toLocaleString('fr-FR')
                      : '—'}
                  </p>
                  <p className="mt-2 text-slate-500">Météo locale</p>
                  <p className="mt-0.5 text-slate-100">En cours de mise à jour, disponible prochainement.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setMainView('interventions')}
                  className="w-full rounded-md border border-sky-500/55 bg-sky-500/15 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-sky-100 hover:bg-sky-500/25"
                >
                  Plus d'infos
                </button>
              </div>
            </section>
          </aside>

          <main className="grid min-h-0 grid-rows-[minmax(0,1fr),280px] gap-3">
            <section className="relative min-h-0 overflow-hidden rounded-lg border border-slate-700/75 bg-slate-900 shadow-xl shadow-black/30">
              <WorldMap
                buildings={buildings}
                showInterventionSectors={showInterventionSectors}
                interventions={interventions}
                liveVehicles={liveVehicleTracks}
              />
              <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-slate-950/55 to-transparent" />
              <div className="absolute bottom-3 right-3 z-[500] rounded-lg border border-slate-600/70 bg-slate-950/90 px-3 py-2 text-[10px] text-slate-200 shadow-lg shadow-black/35">
                <p className="mb-1 font-semibold uppercase tracking-[0.12em] text-slate-300">Légende véhicules live</p>
                <div className="space-y-1">
                  <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full border border-rose-100 bg-rose-500" />En route / mission</div>
                  <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full border border-amber-100 bg-amber-500" />Retour hors secteur</div>
                  <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full border border-emerald-100 bg-emerald-500" />Disponible radio</div>
                  <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full border border-sky-100 bg-sky-500" />Transport / arrivée hôpital</div>
                  <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full border border-yellow-100 bg-yellow-500" />Préalerte</div>
                </div>
              </div>

              {contextMenu && (
                <ContextMenu
                  x={contextMenu.x}
                  y={contextMenu.y}
                  onClose={() => setContextMenu(null)}
                  onBuildClick={(buildingType, buildingName) =>
                    handleBuildingPlace(contextMenu.lat, contextMenu.lng, buildingType, buildingName)
                  }
                />
              )}
            </section>

            <section className="grid grid-cols-[1.25fr,1fr,1fr] gap-3 text-[11px]">
              <div className="rounded-lg border border-slate-700/85 bg-slate-900 p-3.5 shadow-xl shadow-black/35">
                <p className="text-[10px] uppercase tracking-[0.12em] text-red-300">Alertes et notifications</p>
                <div className="mt-2 space-y-1.5 text-slate-300">
                  <p className="rounded border border-red-500/30 bg-red-950/40 px-2 py-1">Zone bloquée · Avenue des Frères Lumière</p>
                  <p className="rounded border border-amber-500/30 bg-amber-950/30 px-2 py-1">Module météo en cours de mise à jour, disponible prochainement.</p>
                  <p className="rounded border border-sky-500/30 bg-sky-950/30 px-2 py-1">
                    Demande d'assistance SAMU · {Math.max(0, activeInterventions.length - 1)} en attente
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setMainView('interventions')}
                  className="mt-2 w-full rounded border border-slate-600/70 bg-slate-800/80 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-200"
                >
                  Voir toutes les alertes
                </button>
              </div>
              <div className="rounded-lg border border-slate-700/85 bg-slate-900 p-3.5 shadow-xl shadow-black/35">
                <p className="text-[10px] uppercase tracking-[0.12em] text-emerald-300">Ressources globales</p>
                <div className="mt-2 space-y-2 text-slate-300">
                  <div className="rounded border border-slate-700/70 bg-slate-950/80 px-2.5 py-1.5">
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">Flotte véhicules</p>
                    <p className="mt-0.5 text-sm font-semibold text-slate-100">
                      {availableVehicles} <span className="text-xs font-normal text-emerald-300">dispo.</span>
                    </p>
                  </div>
                  <div className="rounded border border-slate-700/70 bg-slate-950/80 px-2.5 py-1.5">
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">Effectif personnel</p>
                    <p className="mt-0.5 text-sm font-semibold text-slate-100">
                      {availablePersonnel} <span className="text-xs font-normal text-emerald-300">actifs</span>
                    </p>
                  </div>
                </div>
                <div className="mt-2.5 rounded border border-slate-700/70 bg-slate-950/75 p-2">
                  <div className="mb-1 flex items-center justify-between text-[10px] text-slate-500">
                    <span>Taux de disponibilité</span>
                    <span>{availabilityPercent}%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative h-16 w-16 shrink-0">
                      <svg viewBox="0 0 80 80" className="h-16 w-16 -rotate-90">
                        <circle cx="40" cy="40" r="30" stroke="rgba(51,65,85,0.9)" strokeWidth="8" fill="none" />
                        <circle
                          cx="40"
                          cy="40"
                          r="30"
                          stroke="rgba(16,185,129,0.9)"
                          strokeWidth="8"
                          strokeLinecap="round"
                          fill="none"
                          strokeDasharray={availabilityCircumference}
                          strokeDashoffset={availabilityOffset}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-emerald-300">
                        {availabilityPercent}%
                      </div>
                    </div>
                    <div className="min-w-0 text-[10px] text-slate-400">
                      <p>Disponibilité opérationnelle</p>
                      <p className="mt-0.5 text-emerald-300">Capacité de réponse conforme</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-slate-700/85 bg-slate-900 p-3.5 shadow-xl shadow-black/35">
                <p className="text-[10px] uppercase tracking-[0.12em] text-sky-300">Alliances actives</p>
                <div className="mt-2 rounded border border-slate-700/70 bg-slate-950/75 px-2 py-2 text-slate-300">
                  En cours de mise à jour, disponible prochainement.
                </div>
                <button
                  type="button"
                  onClick={() => handleFutureFeature('Alliances')}
                  className="mt-2 w-full rounded border border-slate-700/70 bg-slate-800/50 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400"
                >
                  Voir toutes les alliances
                </button>
              </div>
            </section>
          </main>

          <aside className="flex min-h-0 flex-col gap-3">
            <section className="rounded-lg border border-slate-700/75 bg-slate-900/90 shadow-xl shadow-black/25">
              <div className="border-b border-slate-700/70 px-3 py-2">
                <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-100">Engager des ressources</h2>
              </div>
              <div className="space-y-3 p-3">
                <div className="grid grid-cols-3 gap-2 text-center text-[11px]">
                  <div className="rounded-md border border-red-500/35 bg-red-600/10 px-2 py-2">
                    <p className="font-semibold text-red-200">FPT</p>
                    <p className="mt-1 text-red-300">Prêt</p>
                  </div>
                  <div className="rounded-md border border-orange-500/35 bg-orange-600/10 px-2 py-2">
                    <p className="font-semibold text-orange-200">VSAV</p>
                    <p className="mt-1 text-orange-300">Prêt</p>
                  </div>
                  <div className="rounded-md border border-sky-500/35 bg-sky-600/10 px-2 py-2">
                    <p className="font-semibold text-sky-200">EPA</p>
                    <p className="mt-1 text-sky-300">Prêt</p>
                  </div>
                </div>

                <div className="rounded-md border border-slate-700/80 bg-slate-950/70 p-2.5">
                  <div className="mb-2 flex items-center justify-between text-[11px]">
                    <span className="uppercase tracking-wide text-slate-400">Unités disponibles ({availableUnitsRows.length})</span>
                    <span className="font-semibold text-emerald-300">
                      {new Set(playerVehicles.map((v) => Number(v.batiment_id || 0)).filter((id) => id > 0)).size || 0} bases
                    </span>
                  </div>
                  <div className="mb-2 grid grid-cols-2 gap-1.5">
                    <input
                      type="search"
                      value={unitsSearch}
                      onChange={(e) => setUnitsSearch(e.target.value)}
                      placeholder="Rechercher une unité..."
                      className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1 text-[11px] text-slate-100 placeholder:text-slate-500 outline-none focus:border-emerald-500/60"
                    />
                    <select
                      value={buildingFilter}
                      onChange={(e) => {
                        const v = e.target.value;
                        setBuildingFilter(v === 'all' ? 'all' : Number(v));
                      }}
                      className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1 text-[11px] text-slate-100 outline-none focus:border-emerald-500/60"
                    >
                      <option value="all">Tous les bâtiments</option>
                      {buildingFilterOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={vehicleTypeFilter}
                      onChange={(e) => setVehicleTypeFilter(e.target.value)}
                      className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1 text-[11px] text-slate-100 outline-none focus:border-emerald-500/60"
                    >
                      <option value="all">Tous les types</option>
                      {vehicleTypeFilterOptions.map((typeName) => (
                        <option key={typeName} value={typeName}>
                          {typeName}
                        </option>
                      ))}
                    </select>
                    <select
                      value={unitsFilter}
                      onChange={(e) => setUnitsFilter(e.target.value as 'all' | 'disponible' | 'en_mission' | 'en_maintenance')}
                      className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1 text-[11px] text-slate-100 outline-none focus:border-emerald-500/60"
                    >
                      <option value="all">Statut: tous</option>
                      <option value="disponible">Prêts</option>
                      <option value="en_mission">Mission</option>
                      <option value="en_maintenance">Maintenance</option>
                    </select>
                  </div>
                  <div className="overflow-hidden rounded-md border border-slate-700/70 bg-slate-900/90">
                    <div className="grid grid-cols-[minmax(0,1.3fr),minmax(0,1fr),minmax(0,0.9fr),56px,minmax(0,1fr),48px] border-b border-slate-700/70 bg-slate-950/85 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400">
                      <span>Unité</span>
                      <span>Type</span>
                      <span>Service</span>
                      <span className="text-center">Eq.</span>
                      <span>État</span>
                      <span className="text-right">Disp.</span>
                    </div>
                    <div className="max-h-44 overflow-y-auto text-[11px]">
                      {availableUnitsRows.map((row) => (
                        <div
                          key={row.id}
                          className="grid grid-cols-[minmax(0,1.3fr),minmax(0,1fr),minmax(0,0.9fr),56px,minmax(0,1fr),48px] items-center gap-1 border-b border-slate-800/65 px-2 py-1.5 last:border-b-0"
                        >
                          <span className="truncate pr-1 text-slate-200">{row.unit}</span>
                          <span className="truncate text-slate-400">{row.type}</span>
                          <span className="truncate text-slate-300">{row.service}</span>
                          <span className="text-center font-semibold text-slate-200">{row.crew}</span>
                          <span className={row.state === 'PRÊT' ? 'text-emerald-300' : 'text-amber-300'}>{row.state}</span>
                          <span className="text-right text-slate-300">{row.eta}</span>
                        </div>
                      ))}
                      {availableUnitsRows.length === 0 ? (
                        <p className="px-2 py-3 text-center text-slate-500">Aucune unité correspondant au filtre</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="min-h-0 flex-1 rounded-lg border border-slate-700/75 bg-slate-900/90 shadow-xl shadow-black/25">
              <div className="border-b border-slate-700/70 px-3 py-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-100">Exigences de l'intervention</h3>
              </div>
              <div className="space-y-3 p-3 text-xs">
                <div className="rounded-md border border-slate-700/80 bg-slate-950/70 p-2.5">
                  <p className="text-slate-500">Niveau de risque</p>
                  <p className="mt-1 font-semibold text-red-300">
                    {selectedIntervention ? (getInterventionPhaseUiKey(selectedIntervention) === 'wait' ? 'Très élevé' : 'Élevé') : '—'}
                  </p>
                </div>
                <div className="rounded-md border border-slate-700/80 bg-slate-950/70 p-2.5">
                  <p className="text-slate-500">Personnel disponible</p>
                  <p className="mt-1 font-semibold text-emerald-300">{availablePersonnel.toLocaleString('fr-FR')} agents</p>
                </div>
                <div className="rounded-md border border-slate-700/80 bg-slate-950/70 p-2.5">
                  <p className="text-slate-500">Unités de l'intervention</p>
                  <div className="mt-1.5 space-y-1">
                    {selectedInterventionVehicleRows.length > 0 ? (
                      selectedInterventionVehicleRows.slice(0, 4).map((v) => (
                        <div key={v.id} className="flex items-center justify-between rounded border border-slate-700/70 bg-slate-900/80 px-2 py-1">
                          <span className="truncate pr-2 text-slate-200">{v.nom}</span>
                          <span className="text-[10px] text-emerald-300">ENGAGÉE</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-[11px] text-slate-500">Aucune unité encore engagée.</p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setMainView('interventions')}
                  className="w-full rounded-md border border-emerald-500/60 bg-emerald-600/85 px-3 py-2.5 text-xs font-bold uppercase tracking-wide text-white hover:bg-emerald-500"
                >
                  Engager les ressources
                </button>
              </div>
            </section>
          </aside>
        </div>
        ) : null}
        {activeMainView === 'resources' ? (
          <div className="grid h-full min-h-0 grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.45fr),minmax(0,1fr)]">
            <section className="min-h-0 rounded-lg border border-slate-700/75 bg-slate-900/90 p-3 shadow-xl shadow-black/30">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-emerald-200">Vue ressources avancée</h2>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={openInterventionsSynoptic}
                    className="rounded border border-red-500/45 bg-red-600/20 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-red-100 hover:bg-red-600/30"
                  >
                    Synoptique
                  </button>
                  <span className="rounded border border-emerald-500/50 bg-emerald-500/15 px-2 py-1 text-[10px] font-semibold text-emerald-200">
                    {availableUnitsRows.length} unités filtrées
                  </span>
                  <button
                    type="button"
                    onClick={() => openDedicatedMainView('resources')}
                    className="rounded border border-slate-600/70 bg-slate-800/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-200 hover:bg-slate-700/80"
                  >
                    Ouvrir en page
                  </button>
                </div>
              </div>

              <div className="mb-3 grid grid-cols-2 gap-2 text-[11px] xl:grid-cols-4">
                <div className="rounded border border-slate-700/70 bg-slate-950/70 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500">Flotte totale</p>
                  <p className="mt-1 text-lg font-semibold text-slate-100">{vehicleStatusMetrics.total}</p>
                </div>
                <div className="rounded border border-emerald-500/35 bg-emerald-950/20 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-emerald-300">Prêts au départ</p>
                  <p className="mt-1 text-lg font-semibold text-emerald-200">{vehicleStatusMetrics.pret}</p>
                </div>
                <div className="rounded border border-amber-500/35 bg-amber-950/20 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-amber-300">En mission</p>
                  <p className="mt-1 text-lg font-semibold text-amber-200">{vehicleStatusMetrics.mission}</p>
                </div>
                <div className="rounded border border-sky-500/35 bg-sky-950/20 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-sky-300">Maint./panne</p>
                  <p className="mt-1 text-lg font-semibold text-sky-200">{vehicleStatusMetrics.maintenance + vehicleStatusMetrics.panne}</p>
                </div>
              </div>

              <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                <input
                  type="search"
                  value={unitsSearch}
                  onChange={(e) => setUnitsSearch(e.target.value)}
                  placeholder="Rechercher une unité..."
                  className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1.5 text-[11px] text-slate-100 placeholder:text-slate-500 outline-none focus:border-emerald-500/60"
                />
                <select
                  value={buildingFilter}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBuildingFilter(v === 'all' ? 'all' : Number(v));
                  }}
                  className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1.5 text-[11px] text-slate-100 outline-none focus:border-emerald-500/60"
                >
                  <option value="all">Tous les bâtiments</option>
                  {buildingFilterOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                    </option>
                  ))}
                </select>
                <select
                  value={vehicleTypeFilter}
                  onChange={(e) => setVehicleTypeFilter(e.target.value)}
                  className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1.5 text-[11px] text-slate-100 outline-none focus:border-emerald-500/60"
                >
                  <option value="all">Tous les types</option>
                  {vehicleTypeFilterOptions.map((typeName) => (
                    <option key={typeName} value={typeName}>
                      {typeName}
                    </option>
                  ))}
                </select>
                <select
                  value={unitsFilter}
                  onChange={(e) => setUnitsFilter(e.target.value as 'all' | 'disponible' | 'en_mission' | 'en_maintenance')}
                  className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1.5 text-[11px] text-slate-100 outline-none focus:border-emerald-500/60"
                >
                  <option value="all">Statut: tous</option>
                  <option value="disponible">Prêts</option>
                  <option value="en_mission">Mission</option>
                  <option value="en_maintenance">Maintenance</option>
                </select>
              </div>

              <div className="overflow-hidden rounded-md border border-slate-700/70 bg-slate-950/85">
                <div className="grid grid-cols-[minmax(0,1.2fr),minmax(0,0.85fr),minmax(0,0.85fr),58px,minmax(0,1fr),70px] border-b border-slate-700/70 bg-slate-900/70 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400">
                  <span>Unité</span>
                  <span>Type</span>
                  <span>Service</span>
                  <span className="text-center">Eq.</span>
                  <span>État</span>
                  <span className="text-right">ETA</span>
                </div>
                <div className="max-h-[54vh] overflow-y-auto text-[11px]">
                  {availableUnitsRows.map((row) => (
                    <div
                      key={row.id}
                      className="grid grid-cols-[minmax(0,1.2fr),minmax(0,0.85fr),minmax(0,0.85fr),58px,minmax(0,1fr),70px] items-center gap-1 border-b border-slate-800/65 px-2 py-2 last:border-b-0"
                    >
                      <span className="truncate pr-1 text-slate-200">{row.unit}</span>
                      <span className="truncate text-slate-400">{row.type}</span>
                      <span className="truncate text-slate-300">{row.service}</span>
                      <span className="text-center font-semibold text-slate-200">{row.crew}</span>
                      <span className={row.state === 'PRÊT' ? 'text-emerald-300' : 'text-amber-300'}>{row.state}</span>
                      <span className="text-right text-slate-300">{row.eta}</span>
                    </div>
                  ))}
                  {availableUnitsRows.length === 0 ? (
                    <p className="px-2 py-4 text-center text-slate-500">Aucune unité correspondant au filtre</p>
                  ) : null}
                </div>
              </div>
            </section>

            <section className="grid min-h-0 grid-rows-[auto,auto,1fr] gap-3">
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">Disponibilité flotte</p>
                  <p className="mt-1 text-lg font-semibold text-emerald-300">{vehicleStatusMetrics.readinessPercent}%</p>
                  <p className="text-[10px] text-slate-400">{vehicleStatusMetrics.pret}/{vehicleStatusMetrics.total} prêts</p>
                </div>
                <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">Taux RH actif</p>
                  <p className="mt-1 text-lg font-semibold text-emerald-300">{personnelMetrics.activeRate}%</p>
                  <p className="text-[10px] text-slate-400">{personnelMetrics.actif}/{personnelMetrics.total} agents actifs</p>
                </div>
                <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">Km moyen flotte</p>
                  <p className="mt-1 text-lg font-semibold text-slate-100">{vehicleStatusMetrics.avgKm.toLocaleString('fr-FR')}</p>
                  <p className="text-[10px] text-slate-400">Total {vehicleStatusMetrics.totalKm.toLocaleString('fr-FR')} km</p>
                </div>
                <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">Capacité équipages</p>
                  <p className="mt-1 text-lg font-semibold text-slate-100">{vehicleStatusMetrics.totalCapacity}</p>
                  <p className="text-[10px] text-slate-400">{vehicleStatusMetrics.premium} unités premium</p>
                </div>
              </div>

              <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
                <p className="mb-2 text-[10px] uppercase tracking-[0.12em] text-amber-300">Alertes opérationnelles</p>
                <div className="space-y-1.5 text-[11px]">
                  {resourceAlerts.length > 0 ? (
                    resourceAlerts.map((alert) => (
                      <p key={alert} className="rounded border border-amber-500/30 bg-amber-950/25 px-2 py-1 text-amber-100">
                        {alert}
                      </p>
                    ))
                  ) : (
                    <p className="rounded border border-emerald-500/30 bg-emerald-950/20 px-2 py-1 text-emerald-200">
                      Situation stable : aucune tension critique detectee.
                    </p>
                  )}
                </div>
              </div>

              <div className="grid min-h-0 grid-rows-[auto,1fr] gap-2">
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-2.5">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Personnel inactif</p>
                    <p className="mt-1 text-sm font-semibold text-slate-100">{personnelMetrics.inactif}</p>
                  </div>
                  <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-2.5">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Age moyen</p>
                    <p className="mt-1 text-sm font-semibold text-slate-100">{personnelMetrics.avgAge} ans</p>
                  </div>
                </div>
                <div className="grid min-h-0 grid-cols-1 gap-2 xl:grid-cols-2">
                  <div className="min-h-0 rounded-lg border border-slate-700/80 bg-slate-900/90 p-2.5">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400">
                        Interventions actives ({activeInterventions.length})
                      </p>
                      <button
                        type="button"
                        onClick={openInterventionsSynoptic}
                        className="rounded border border-red-500/40 bg-red-600/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-100 hover:bg-red-600/25"
                      >
                        Gestion complète
                      </button>
                    </div>
                    <div className="max-h-[24vh] space-y-1.5 overflow-y-auto">
                      {activeInterventions.map((inv) => {
                        const lines = getInterventionDisplayLines(inv, 'Intervention');
                        return (
                          <button
                            key={inv.id}
                            type="button"
                            onClick={() => {
                              setSelectedInterventionId(inv.id);
                              setMainView('dispatch');
                            }}
                            className="w-full rounded border border-slate-700/70 bg-slate-950/70 px-2 py-1.5 text-left hover:bg-slate-800/80"
                          >
                            <p className="truncate text-xs font-semibold text-slate-100">{lines.headline}</p>
                            <p className="truncate text-[10px] text-slate-400">{lines.subline || 'Adresse non précisée'}</p>
                          </button>
                        );
                      })}
                      {activeInterventions.length === 0 ? (
                        <p className="text-[11px] text-slate-500">Aucune intervention active.</p>
                      ) : null}
                    </div>
                  </div>
                  <div className="min-h-0 rounded-lg border border-slate-700/80 bg-slate-900/90 p-2.5">
                    <p className="mb-2 text-[10px] uppercase tracking-wide text-slate-400">Top kilométrage</p>
                    <div className="max-h-[24vh] space-y-1.5 overflow-y-auto text-[11px]">
                      {topMileageVehicles.length > 0 ? (
                        topMileageVehicles.map((v) => (
                          <div key={v.id} className="rounded border border-slate-700/70 bg-slate-950/70 px-2 py-1.5">
                            <p className="truncate font-semibold text-slate-100">{v.nom || `Unité ${v.id}`}</p>
                            <p className="text-[10px] text-slate-400">
                              {String(v.type || 'Type inconnu')} - {Number(v.kilometrage || 0).toLocaleString('fr-FR')} km
                            </p>
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-500">Aucun véhicule enregistré.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        ) : null}
        {activeMainView === 'interventions' ? (
          <div className="grid h-full min-h-0 grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.5fr),minmax(0,1fr)]">
            <section className="min-h-0 rounded-lg border border-slate-700/75 bg-slate-900/90 p-3 shadow-xl shadow-black/30">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-red-200">
                  Synoptique interventions
                </h2>
                <div className="flex items-center gap-1.5">
                  <span className="rounded border border-red-500/45 bg-red-500/15 px-2 py-1 text-[10px] font-semibold text-red-100">
                    {filteredInterventions.length} / {activeInterventions.length}
                  </span>
                  <button
                    type="button"
                    onClick={() => openDedicatedMainView('interventions')}
                    className="rounded border border-slate-600/70 bg-slate-800/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-200 hover:bg-slate-700/80"
                  >
                    Ouvrir en page
                  </button>
                  <button
                    type="button"
                    onClick={() => setMainView('dispatch')}
                    className="rounded border border-red-500/45 bg-red-600/20 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-red-100 hover:bg-red-600/30"
                  >
                    Gestion dispatch
                  </button>
                </div>
              </div>

              <div className="mb-3 grid grid-cols-2 gap-2 text-[11px] xl:grid-cols-6">
                <div className="rounded border border-red-500/35 bg-red-950/20 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-red-300">Actives</p>
                  <p className="mt-1 text-lg font-semibold text-red-100">{activeInterventions.length}</p>
                </div>
                <div className="rounded border border-red-500/35 bg-red-950/20 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-red-300">Attente</p>
                  <p className="mt-1 text-lg font-semibold text-red-100">{interventionPhaseCounts.wait}</p>
                </div>
                <div className="rounded border border-amber-500/35 bg-amber-950/20 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-amber-300">Transit</p>
                  <p className="mt-1 text-lg font-semibold text-amber-100">{interventionPhaseCounts.travel}</p>
                </div>
                <div className="rounded border border-emerald-500/35 bg-emerald-950/20 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-emerald-300">Sur site</p>
                  <p className="mt-1 text-lg font-semibold text-emerald-100">{interventionPhaseCounts.site}</p>
                </div>
                <div className="rounded border border-sky-500/35 bg-sky-950/20 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-sky-300">Unités engagées</p>
                  <p className="mt-1 text-lg font-semibold text-sky-100">
                    {activeInterventions.reduce((sum, inv) => sum + Number(inv.dispatch_total || 0), 0)}
                  </p>
                </div>
                <div className="rounded border border-slate-500/35 bg-slate-950/20 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-slate-300">Arrivée estimée</p>
                  <p className="mt-1 text-lg font-semibold text-slate-100">
                    {Math.max(
                      0,
                      ...activeInterventions.map((inv) => Math.ceil(Number(inv.travel_estimate_sec || 0) / 60))
                    )} min
                  </p>
                </div>
              </div>

              <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-4">
                <input
                  type="search"
                  value={interventionSearch}
                  onChange={(e) => setInterventionSearch(e.target.value)}
                  placeholder="Rechercher numéro / mission / adresse..."
                  className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1.5 text-[11px] text-slate-100 placeholder:text-slate-500 outline-none focus:border-red-500/60 md:col-span-2"
                />
                <select
                  value={interventionPhaseFilter}
                  onChange={(e) => setInterventionPhaseFilter(e.target.value as 'all' | 'wait' | 'travel' | 'site')}
                  className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1.5 text-[11px] text-slate-100 outline-none focus:border-red-500/60"
                >
                  <option value="all">Phase: toutes</option>
                  <option value="wait">En attente</option>
                  <option value="travel">En transit</option>
                  <option value="site">Sur site</option>
                </select>
                <select
                  value={interventionSort}
                  onChange={(e) => setInterventionSort(e.target.value as 'priority' | 'newest' | 'oldest')}
                  className="rounded border border-slate-700/70 bg-slate-900/85 px-2 py-1.5 text-[11px] text-slate-100 outline-none focus:border-red-500/60"
                >
                  <option value="priority">Tri: priorité</option>
                  <option value="newest">Tri: plus récent</option>
                  <option value="oldest">Tri: plus ancien</option>
                </select>
              </div>

              <div className="overflow-hidden rounded-md border border-slate-700/70 bg-slate-950/85">
                <div className="grid grid-cols-[90px,minmax(0,1.1fr),minmax(0,0.95fr),90px,90px,100px,120px,110px] border-b border-slate-700/70 bg-slate-900/70 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400">
                  <span>N°</span>
                  <span>Mission</span>
                  <span>Adresse</span>
                  <span className="text-center">Phase</span>
                  <span className="text-center">Priorité</span>
                  <span className="text-center">Unités</span>
                  <span className="text-right">Créée le</span>
                  <span className="text-center">Action</span>
                </div>
                <div className="max-h-[56vh] overflow-y-auto text-[11px]">
                  {filteredInterventions.map((inv) => {
                    const lines = getInterventionDisplayLines(inv, 'Intervention');
                    const phase = getInterventionPhaseUiKey(inv);
                    const priority = getInterventionPriority(inv);
                    return (
                      <div
                        key={inv.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedInterventionId(inv.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedInterventionId(inv.id);
                          }
                        }}
                        className={`grid w-full grid-cols-[90px,minmax(0,1.1fr),minmax(0,0.95fr),90px,90px,100px,120px,110px] items-center gap-1 border-b border-slate-800/65 px-2 py-2 text-left last:border-b-0 ${
                          selectedIntervention?.id === inv.id ? 'bg-red-950/20' : 'hover:bg-slate-800/70'
                        }`}
                      >
                        <span className="truncate font-semibold text-slate-200">#{inv.numero_intervention || inv.id}</span>
                        <span className="truncate text-slate-100">{lines.headline}</span>
                        <span className="truncate text-slate-400">{lines.subline || 'Adresse non précisée'}</span>
                        <span className={`mx-auto w-fit rounded border px-2 py-0.5 text-[10px] font-semibold ${interventionPhaseClass(phase)}`}>
                          {interventionPhaseLabel(phase)}
                        </span>
                        <span className="mx-auto">
                          <input
                            type="number"
                            min={1}
                            step={1}
                            value={priority}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setInterventionPriority(inv, Number(e.target.value))}
                            className="w-14 rounded border border-red-500/45 bg-slate-900/95 px-1.5 py-0.5 text-center text-[10px] font-semibold text-red-200 outline-none focus:border-red-400"
                          />
                        </span>
                        <span className="text-center text-slate-300">
                          {Number(inv.dispatch_total || 0)} ({Number(inv.dispatch_sur_place || 0)} sur site)
                        </span>
                        <span className="text-right text-[10px] text-slate-400">
                          {inv.date_creation ? new Date(inv.date_creation).toLocaleString('fr-FR') : '—'}
                        </span>
                        <span className="text-center">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedInterventionId(inv.id);
                              manageController.setManageId(inv.id);
                            }}
                            className="rounded border border-emerald-500/50 bg-emerald-600/20 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-100 hover:bg-emerald-600/30"
                          >
                            Engager
                          </button>
                        </span>
                      </div>
                    );
                  })}
                  {filteredInterventions.length === 0 ? (
                    <p className="px-2 py-5 text-center text-slate-500">
                      Aucune intervention correspondant aux filtres.
                    </p>
                  ) : null}
                </div>
              </div>
            </section>

            <section className="grid min-h-0 grid-rows-[auto,1fr] gap-3">
              <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
                <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">Intervention sélectionnée</p>
                <p className="mt-1 truncate text-sm font-semibold text-slate-100">{selectedDisplay?.headline || 'Aucune intervention'}</p>
                <p className="mt-0.5 truncate text-[11px] text-slate-400">{selectedDisplay?.subline || '—'}</p>
                {selectedIntervention ? (
                  <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                    <div className="rounded border border-slate-700/70 bg-slate-950/70 p-2">
                      <p className="text-[10px] uppercase tracking-wide text-slate-500">Phase</p>
                      <p className="mt-1 text-slate-100">{interventionPhaseLabel(getInterventionPhaseUiKey(selectedIntervention))}</p>
                    </div>
                    <div className="rounded border border-slate-700/70 bg-slate-950/70 p-2">
                      <p className="text-[10px] uppercase tracking-wide text-slate-500">Priorité</p>
                      <p className="mt-1 text-slate-100">{getInterventionPriority(selectedIntervention)}</p>
                    </div>
                    <div className="rounded border border-slate-700/70 bg-slate-950/70 p-2">
                      <p className="text-[10px] uppercase tracking-wide text-slate-500">En route</p>
                      <p className="mt-1 text-slate-100">{Number(selectedIntervention.dispatch_en_route || 0)}</p>
                    </div>
                    <div className="rounded border border-slate-700/70 bg-slate-950/70 p-2">
                      <p className="text-[10px] uppercase tracking-wide text-slate-500">Sur place</p>
                      <p className="mt-1 text-slate-100">{Number(selectedIntervention.dispatch_sur_place || 0)}</p>
                    </div>
                  </div>
                ) : null}
                {selectedIntervention ? (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (!selectedIntervention) return;
                        manageController.setManageId(selectedIntervention.id);
                      }}
                      className="rounded border border-emerald-500/55 bg-emerald-600/25 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-100 hover:bg-emerald-600/35"
                    >
                      Ouvrir engagement détaillé
                    </button>
                    <button
                      type="button"
                      onClick={() => setMainView('resources')}
                      className="rounded border border-sky-500/55 bg-sky-600/20 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-sky-100 hover:bg-sky-600/30"
                    >
                      Voir ressources
                    </button>
                  </div>
                ) : null}
              </div>
              <div className="min-h-0 rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
                <p className="mb-2 text-[10px] uppercase tracking-[0.12em] text-slate-400">Unités engagées</p>
                <div className="max-h-[48vh] space-y-1.5 overflow-y-auto text-[11px]">
                  {selectedInterventionVehicleRows.length > 0 ? (
                    selectedInterventionVehicleRows.map((v) => (
                      <div key={v.id} className="rounded border border-slate-700/70 bg-slate-950/70 px-2 py-1.5">
                        <p className="truncate font-semibold text-slate-100">{v.nom || `Unité ${v.id}`}</p>
                        <p className="text-[10px] text-slate-400">
                          {String(v.type || 'Type inconnu')} · {vehicleStateLabel(v.statut)}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-500">Aucune unité engagée sur cette intervention.</p>
                  )}
                </div>
              </div>
            </section>
          </div>
        ) : null}
        {activeMainView === 'buildings' ? (
          <div className="grid h-full min-h-0 grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.45fr),minmax(0,1fr)]">
            <section className="min-h-0 rounded-lg border border-slate-700/75 bg-slate-900/90 p-3 shadow-xl shadow-black/30">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-sky-200">Vue bâtiments avancée</h2>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={openInterventionsSynoptic}
                    className="rounded border border-red-500/45 bg-red-600/20 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-red-100 hover:bg-red-600/30"
                  >
                    Synoptique
                  </button>
                  <button
                    type="button"
                    onClick={() => openDedicatedMainView('buildings')}
                    className="rounded border border-slate-600/70 bg-slate-800/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-200 hover:bg-slate-700/80"
                  >
                    Ouvrir en page
                  </button>
                  <button
                    type="button"
                    onClick={handleBuildingsClick}
                    className="rounded-md border border-sky-500/55 bg-sky-600/20 px-2.5 py-1 text-[10px] font-semibold uppercase text-sky-100 hover:bg-sky-600/30"
                  >
                    Gérer les bâtiments
                  </button>
                </div>
              </div>
              <div className="mb-3 grid grid-cols-2 gap-2 text-[11px] xl:grid-cols-4">
                <div className="rounded border border-slate-700/70 bg-slate-950/70 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500">Bâtiments actifs</p>
                  <p className="mt-1 text-lg font-semibold text-slate-100">{buildingOperationalRows.length}</p>
                </div>
                <div className="rounded border border-emerald-500/35 bg-emerald-950/20 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-emerald-300">Véhicules rattachés</p>
                  <p className="mt-1 text-lg font-semibold text-emerald-200">
                    {buildingOperationalRows.reduce((sum, row) => sum + row.vehiclesCount, 0)}
                  </p>
                </div>
                <div className="rounded border border-sky-500/35 bg-sky-950/20 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-sky-300">Personnel rattaché</p>
                  <p className="mt-1 text-lg font-semibold text-sky-200">
                    {buildingOperationalRows.reduce((sum, row) => sum + row.personnelCount, 0)}
                  </p>
                </div>
                <div className="rounded border border-amber-500/35 bg-amber-950/20 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-amber-300">Chantiers en cours</p>
                  <p className="mt-1 text-lg font-semibold text-amber-200">
                    {buildingOperationalRows.filter((row) => row.isPersonnelUpgradeRunning || row.isVehicleUpgradeRunning).length}
                  </p>
                </div>
              </div>

              <div className="overflow-hidden rounded-md border border-slate-700/70 bg-slate-950/80">
                <div className="grid grid-cols-[minmax(0,1.15fr),88px,80px,88px,78px,82px,82px] border-b border-slate-700/70 bg-slate-900/70 px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400">
                  <span>Bâtiment</span>
                  <span className="text-center">Niveau</span>
                  <span className="text-center">Véhic.</span>
                  <span className="text-center">Pers.</span>
                  <span className="text-center">Charge V</span>
                  <span className="text-center">Charge P</span>
                  <span className="text-center">XP</span>
                </div>
                <div className="max-h-[58vh] overflow-y-auto">
                  {buildingOperationalRows.length === 0 ? (
                    <p className="px-3 py-6 text-center text-xs text-slate-500">Aucun bâtiment disponible.</p>
                  ) : (
                    buildingOperationalRows.map((row) => (
                      <div key={row.id} className="border-b border-slate-800/65 px-2 py-2 text-[11px] last:border-b-0">
                        <div className="grid grid-cols-[minmax(0,1.15fr),88px,80px,88px,78px,82px,82px] items-center gap-1">
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-slate-100">{row.name}</p>
                            <p className="truncate text-[10px] text-slate-400">{row.typeLabel}</p>
                          </div>
                          <span className="text-center font-semibold text-slate-200">Niv. {row.level}</span>
                          <span className="text-center text-slate-300">{row.vehiclesCount}/{Math.max(0, row.vehicleCapacity)}</span>
                          <span className="text-center text-slate-300">{row.personnelOccupied}/{Math.max(0, row.personnelSlots)}</span>
                          <span className={`text-center font-semibold ${row.vehicleLoadPercent >= 85 ? 'text-red-300' : row.vehicleLoadPercent >= 65 ? 'text-amber-300' : 'text-emerald-300'}`}>
                            {row.vehicleLoadPercent}%
                          </span>
                          <span className={`text-center font-semibold ${row.personnelLoadPercent >= 85 ? 'text-red-300' : row.personnelLoadPercent >= 65 ? 'text-amber-300' : 'text-emerald-300'}`}>
                            {row.personnelLoadPercent}%
                          </span>
                          <span className="text-center text-slate-300">{row.xp}/{row.xpRequired}</span>
                        </div>
                        <div className="mt-1.5 grid grid-cols-1 gap-1 text-[10px] md:grid-cols-3">
                          <p className="rounded border border-slate-700/70 bg-slate-900/70 px-2 py-1 text-slate-300">
                            Flotte prête : <span className="font-semibold text-emerald-300">{row.vehiclesReady}</span>
                          </p>
                          <p className="rounded border border-slate-700/70 bg-slate-900/70 px-2 py-1 text-slate-300">
                            Personnel actif : <span className="font-semibold text-sky-300">{row.personnelActive}</span>
                          </p>
                          <p className="rounded border border-slate-700/70 bg-slate-900/70 px-2 py-1 text-slate-300">
                            Travaux :
                            <span className={`ml-1 font-semibold ${row.isPersonnelUpgradeRunning || row.isVehicleUpgradeRunning ? 'text-amber-300' : 'text-emerald-300'}`}>
                              {row.isPersonnelUpgradeRunning || row.isVehicleUpgradeRunning ? 'EN COURS' : 'RAS'}
                            </span>
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </section>
            <section className="grid min-h-0 grid-rows-[auto,auto,1fr] gap-3">
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">Charge moyenne véhicules</p>
                  <p className="mt-1 text-lg font-semibold text-sky-300">
                    {buildingOperationalRows.length > 0
                      ? Math.round(buildingOperationalRows.reduce((sum, row) => sum + row.vehicleLoadPercent, 0) / buildingOperationalRows.length)
                      : 0}%
                  </p>
                </div>
                <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">Charge moyenne personnel</p>
                  <p className="mt-1 text-lg font-semibold text-sky-300">
                    {buildingOperationalRows.length > 0
                      ? Math.round(buildingOperationalRows.reduce((sum, row) => sum + row.personnelLoadPercent, 0) / buildingOperationalRows.length)
                      : 0}%
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
                <p className="mb-2 text-[10px] uppercase tracking-[0.12em] text-amber-300">Bâtiments sous tension</p>
                <div className="max-h-[18vh] space-y-1.5 overflow-y-auto text-[11px]">
                  {buildingOperationalRows.filter((row) => row.vehicleLoadPercent >= 80 || row.personnelLoadPercent >= 80).slice(0, 6).map((row) => (
                    <div key={`stress-${row.id}`} className="rounded border border-amber-500/35 bg-amber-950/20 px-2 py-1.5">
                      <p className="truncate font-semibold text-amber-100">{row.name}</p>
                      <p className="text-[10px] text-amber-200">
                        Véhicules {row.vehicleLoadPercent}% - Personnel {row.personnelLoadPercent}%
                      </p>
                    </div>
                  ))}
                  {buildingOperationalRows.filter((row) => row.vehicleLoadPercent >= 80 || row.personnelLoadPercent >= 80).length === 0 ? (
                    <p className="rounded border border-emerald-500/30 bg-emerald-950/20 px-2 py-1 text-emerald-200">
                      Aucune saturation critique détectée.
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="min-h-0 rounded-lg border border-slate-700/80 bg-slate-900/90 p-3">
                <p className="mb-2 text-[10px] uppercase tracking-[0.12em] text-slate-400">Répartition par service</p>
                <div className="max-h-[36vh] space-y-1.5 overflow-y-auto text-xs">
                  {buildingTypeBreakdown.map((row) => (
                    <div key={row.typeName} className="rounded border border-slate-700/70 bg-slate-950/75 px-2 py-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-slate-200">{row.typeName}</span>
                        <span className="text-sky-300">{row.count} site(s)</span>
                      </div>
                      <p className="mt-0.5 text-[10px] text-slate-400">
                        {row.vehicles} véhicules - {row.personnel} agents
                      </p>
                    </div>
                  ))}
                  {buildingTypeBreakdown.length === 0 ? (
                    <p className="text-slate-500">Aucun service disponible.</p>
                  ) : null}
                </div>
              </div>
            </section>
          </div>
        ) : null}
      </div>

      <InterventionManageModal
        manageId={manageController.manageId}
        onClose={() => manageController.setManageId(null)}
        instances={interventions}
        availableVehicles={manageController.availableVehicles}
        selectedVehIdsByInstance={manageController.selectedVehIdsByInstance}
        toggleSectorVehicle={manageController.toggleSectorVehicle}
        selectAllSectorVehicles={manageController.selectAllSectorVehicles}
        clearSectorSelection={manageController.clearSectorSelection}
        sectorVehicles={manageController.sectorVehicles}
        sectorAlreadyEngaged={manageController.sectorAlreadyEngaged}
        sectorLoading={manageController.sectorLoading}
        sectorError={manageController.sectorError}
        busyId={manageController.busyId}
        assignVehicle={manageController.assignVehicle}
        advance={manageController.advance}
        unassignReservedVehicle={manageController.unassignReservedVehicle}
        recallInterventionVehicles={manageController.recallInterventionVehicles}
        recallInterventionVehicle={manageController.recallInterventionVehicle}
        reengageInterventionVehicle={manageController.reengageInterventionVehicle}
        handleDeploy={manageController.handleDeploy}
        manageCompletion={manageController.manageCompletion}
        titleLocale={manageController.titleLocale}
      />

      {/* Modal Bâtiments */}
      <BuildingsModal 
        isOpen={showBuildingsModal} 
        onClose={() => setShowBuildingsModal(false)}
        buildings={buildings}
        onBuildingUpdate={(updatedBuilding) => {
          // Mettre à jour le bâtiment dans la liste
          const updatedBuildings = buildings.map(b => 
            b.id === updatedBuilding.id ? updatedBuilding : b
          );
          setBuildings(updatedBuildings);
          // Sauvegarder immédiatement
          saveBuildings(updatedBuildings);
        }}
        onBuildingsChange={(updatedBuildings) => {
          // Mettre à jour la liste complète (pour la suppression)
          setBuildings(updatedBuildings);
          // Sauvegarder immédiatement
          saveBuildings(updatedBuildings);
        }}
      />

      {/* Tutoriel Guidé */}
      <GuidedTutorial
        isActive={showTutorial}
        onClose={() => {
          console.log('Tutoriel fermé par l\'utilisateur');
          setShowTutorial(false);
        }}
        onHighlightElement={setHighlightedElement}
        currentStep={tutorialStep}
        onStepChange={setTutorialStep}
        onAction={(action) => {
            // Actions automatiques pour guider l'utilisateur
            switch (action) {
              case 'open-buildings':
                setTimeout(() => {
                  setShowBuildingsModal(true);
                }, 200);
                break;
              default:
                break;
            }
          }}
      />
    </div>
  );
};

export default Map;

